import { useEffect, useRef } from "react";
import { embedDashboard } from "@superset-ui/embedded-sdk";
import { useImmer } from "use-immer";
import { getSupersetGuestToken } from "../api";
import { errorMessage } from "../../../utils";

const SUPERSET_DOMAIN = "https://insights.btill.app";
const DATASET_IDS = [36, 61];

const getErrorMessage = (error) =>
  error?.response?.data?.message ||
  error?.message ||
  "Failed to embed dashboard";

export const useSupersetDashboard = ({ dashboardId }) => {
  const [state, setState] = useImmer({
    isLoading: true,
    error: null,
  });

  const dashboardRef = useRef(null);
  const guestTokenRef = useRef(null);

  useEffect(() => {
    if (!dashboardRef.current) return;

    let isMounted = true;

    const embed = async () => {
      try {
        setState((draft) => {
          draft.isLoading = true;
          draft.error = null;
        });

        if (!guestTokenRef.current) {
          const tokenData = await getSupersetGuestToken({
            dashboard_id: dashboardId,
            dataset_ids: DATASET_IDS,
          });
          guestTokenRef.current = tokenData.token;
        }

        if (!isMounted) return;

        await embedDashboard({
          id: dashboardId,
          supersetDomain: SUPERSET_DOMAIN,
          mountPoint: dashboardRef.current,
          fetchGuestToken: () => Promise.resolve(guestTokenRef.current),
          dashboardUiConfig: {
            hideTitle: false,
            hideChartControls: true,
            hideTab: false,
            filters: {
              expanded: false,
              visible: true,
            },
          },
          iframeSandboxExtras: [
            "allow-top-navigation",
            "allow-popups-to-escape-sandbox",
          ],
        });

        if (!isMounted) return;

        setState((draft) => {
          draft.isLoading = false;
        });
      } catch (error) {
        if (!isMounted) return;
        const message = getErrorMessage(error);
        errorMessage(message);
        setState((draft) => {
          draft.error = message;
          draft.isLoading = false;
        });
      }
    };

    embed();

    return () => {
      isMounted = false;
    };
  }, [setState, dashboardId]);

  return {
    state,
    dashboardRef,
  };
};
